import {
  ASTNode,
  DirectiveDefinitionNode,
  DocumentNode,
  EnumTypeDefinitionNode,
  EnumTypeExtensionNode,
  EnumValueDefinitionNode,
  FieldDefinitionNode,
  InputObjectTypeDefinitionNode,
  InputObjectTypeExtensionNode,
  InputValueDefinitionNode,
  InterfaceTypeDefinitionNode,
  InterfaceTypeExtensionNode,
  ObjectTypeDefinitionNode,
  ObjectTypeExtensionNode,
  OperationTypeDefinitionNode,
  ScalarTypeDefinitionNode,
  ScalarTypeExtensionNode,
  SchemaDefinitionNode,
  SchemaExtensionNode,
  UnionTypeDefinitionNode,
  UnionTypeExtensionNode,
  isTypeDefinitionNode,
  isTypeExtensionNode,
  visit,
} from 'graphql';
import * as monacoEditor from 'monaco-editor';

import { isMinLengthArray } from 'src/lib/isMinLengthArray';

type OverlappingDefinitionExtensionNode =
  | SchemaDefinitionNode
  | OperationTypeDefinitionNode
  | ScalarTypeDefinitionNode
  | ObjectTypeDefinitionNode
  | FieldDefinitionNode
  | InterfaceTypeDefinitionNode
  | UnionTypeDefinitionNode
  | EnumTypeDefinitionNode
  | EnumValueDefinitionNode
  | InputObjectTypeDefinitionNode
  | DirectiveDefinitionNode
  | SchemaExtensionNode
  | ScalarTypeExtensionNode
  | ObjectTypeExtensionNode
  | InterfaceTypeExtensionNode
  | UnionTypeExtensionNode
  | EnumTypeExtensionNode
  | InputObjectTypeExtensionNode;

const OVERLAPPING_DEFINITION_OR_EXTENSION_KINDS = new Set<ASTNode['kind']>([
  'SchemaDefinition',
  'OperationTypeDefinition',
  'ScalarTypeDefinition',
  'ObjectTypeDefinition',
  'FieldDefinition',
  'InterfaceTypeDefinition',
  'UnionTypeDefinition',
  'EnumTypeDefinition',
  'EnumValueDefinition',
  'InputObjectTypeDefinition',
  'DirectiveDefinition',
  'SchemaExtension',
  'ScalarTypeExtension',
  'ObjectTypeExtension',
  'InterfaceTypeExtension',
  'UnionTypeExtension',
  'EnumTypeExtension',
  'InputObjectTypeExtension',
]);

export function isOverlappingDefinitionOrExtensionNode(
  node: ASTNode | undefined | null,
): node is OverlappingDefinitionExtensionNode {
  return !!node && OVERLAPPING_DEFINITION_OR_EXTENSION_KINDS.has(node.kind);
}

function getNodesPerLine(typeDefs: DocumentNode) {
  // index corresponds to the line number
  const nodesPerLine: ASTNode[][] = [[]];
  visit(typeDefs, {
    enter(node) {
      if (node.loc && isOverlappingDefinitionOrExtensionNode(node)) {
        for (
          let i = node.loc?.startToken.line;
          // TODO: remove this disabling rule for eslint v8 migration
          // eslint-disable-next-line no-unsafe-optional-chaining
          i < node.loc?.endToken.line + 1;
          i++
        ) {
          const lineNodes = nodesPerLine[i] ?? [];
          if (!nodesPerLine[i]) {
            nodesPerLine[i] = lineNodes;
          }
          lineNodes.push(node);
        }
      }
    },
  });
  return nodesPerLine;
}

export function getRangesToRemove({
  typeDefs,
  text,
  showDeprecated,
  showComments,
}: {
  typeDefs: DocumentNode;
  text: string;
  showDeprecated: boolean;
  showComments: boolean;
}) {
  if (showDeprecated && showComments) {
    return [];
  }

  const lines = text.split('\n');
  const nodesPerLine = getNodesPerLine(typeDefs);

  function shouldDeleteDeprecatedNode(
    node:
      | FieldDefinitionNode
      | InputValueDefinitionNode
      | EnumValueDefinitionNode,
  ) {
    if (!node.loc) return false;
    const nodeLocation = node.loc;

    const conflictingNodes = nodesPerLine
      .slice(nodeLocation.startToken.line, nodeLocation.endToken.line + 1)
      .flat();

    const nodesConflict = conflictingNodes.some((nodeOnLine) => {
      // check if the nodeOnLine is a parent that encompasses the node
      if (
        ((node.kind === 'FieldDefinition' ||
          node.kind === 'InputValueDefinition') &&
          (isTypeDefinitionNode(nodeOnLine) ||
            isTypeExtensionNode(nodeOnLine))) ||
        (node.kind === 'EnumValueDefinition' &&
          nodeOnLine.kind === 'EnumTypeDefinition')
      ) {
        if (
          nodeLocation.startToken.line === nodeOnLine.loc?.startToken.line ||
          nodeLocation.endToken.line === nodeOnLine.loc?.endToken.line
        ) {
          // if there is an overlap between the node's line numbers & its parents start or end {}
          // it conflicts
          return true;
        }
      } else if (nodeOnLine !== node) {
        // a conflicting node was found, do not delete
        return true;
      }
      return false;
    });

    return !nodesConflict;
  }

  const rangesToRemove: monacoEditor.Range[] = [];
  // Would be great if this just involved pretty printing a pruned ast,
  // however the graphql print/parser (and ast in general) treat hashtag
  // comments as whitespace, and will strip them out.
  if (!showDeprecated) {
    visit(typeDefs, {
      FieldDefinition(fieldDefinitionNode) {
        if (
          fieldDefinitionNode.directives?.some(
            ({ name }) => name.value === 'deprecated',
          ) &&
          fieldDefinitionNode.loc &&
          shouldDeleteDeprecatedNode(fieldDefinitionNode)
        ) {
          rangesToRemove.push(
            new monacoEditor.Range(
              fieldDefinitionNode.loc.startToken.line,
              fieldDefinitionNode.loc.startToken.column,
              fieldDefinitionNode.loc.endToken.line,
              fieldDefinitionNode.loc.endToken.column,
            ),
          );
        }
      },
      EnumValueDefinition(enumValueDefinitionNode) {
        if (
          enumValueDefinitionNode.directives?.some(
            ({ name }) => name.value === 'deprecated',
          ) &&
          enumValueDefinitionNode.loc &&
          shouldDeleteDeprecatedNode(enumValueDefinitionNode)
        ) {
          rangesToRemove.push(
            new monacoEditor.Range(
              enumValueDefinitionNode.loc.startToken.line,
              enumValueDefinitionNode.loc.startToken.column,
              enumValueDefinitionNode.loc.endToken.line,
              enumValueDefinitionNode.loc.endToken.column,
            ),
          );
        }
      },
    });
  }
  if (!showComments) {
    const stringRanges: monacoEditor.Range[] = [];
    visit(typeDefs, {
      enter(node) {
        if ('description' in node) {
          const description = node.description;
          if (description?.loc) {
            // description.loc.endToken.column is the 1-indexed column *after* the last "
            // description.loc.startToken.column is the 1-indexed column *of* the first "
            const descriptionEndCol = description.loc.endToken.column;
            const descriptionStartCol = description.loc.startToken.column;

            const afterDescription = lines[description.loc.endToken.line - 1] // lines are 1-indexed
              ?.substring(descriptionEndCol)
              .trim();
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const beforeDescription = lines[
              description.loc.startToken.line - 1
            ]! // lines are 1-indexed
              .substring(0, descriptionStartCol - 1) // column is 1-indexed, and this is the first "
              .trim();

            // There is nothing that comes before or after the end column of this description (except a comment after that should be deleted)
            if (
              (afterDescription?.length === 0 ||
                afterDescription?.[0] === '#') &&
              beforeDescription.length === 0
            ) {
              rangesToRemove.push(
                new monacoEditor.Range(
                  description.loc.startToken.line,
                  descriptionStartCol,
                  description.loc.endToken.line,
                  descriptionEndCol,
                ),
              );
            }
          }
        }
      },
      StringValue(stringValueNode) {
        if (stringValueNode.loc) {
          stringRanges.push(
            new monacoEditor.Range(
              stringValueNode.loc.startToken.line,
              stringValueNode.loc.start,
              stringValueNode.loc.endToken.line,
              stringValueNode.loc.end,
            ),
          );
        }
      },
    });

    // As we iterate through all the matches, make sure no #'s are found inside
    // strings. If they are, ignore them. In order to not compare against all
    // the string ranges, keep only the ranges after the current text match.
    let restOfStringRanges = [...stringRanges];

    lines.forEach((line, lineNumberOfComment) => {
      // Right now, there is no way to delete parts of lines in monaco with setHiddenAreas
      // if a comment is not the only this on that line, (maybe valuable gql on that line)
      // we don't want to delete it
      const comment = line.match(/^\s*#.*/);
      if (!isMinLengthArray(1, comment)) return;
      const [fullMatch] = comment;
      const startIndexOfComment = line.indexOf('#');
      const foundOverlap = restOfStringRanges.some(
        (stringRange, stringRangeIndex) => {
          if (
            stringRange.containsPosition(
              new monacoEditor.Position(
                lineNumberOfComment,
                startIndexOfComment + 1,
              ),
            )
          ) {
            restOfStringRanges = restOfStringRanges.slice(stringRangeIndex);
            return true; // the comment is inside the string
          }
          return false;
        },
      );
      if (foundOverlap) {
        // Comment is inside string
        return;
      }

      rangesToRemove.push(
        new monacoEditor.Range(
          // 1 - indexed line numbers & columns
          lineNumberOfComment + 1,
          startIndexOfComment + 1,
          lineNumberOfComment + 1,
          startIndexOfComment + fullMatch.trim().length + 1,
        ),
      );
    });
  }

  const sortedRangesToRemove = rangesToRemove.sort(
    (range1, range2) => range1.startLineNumber - range2.startLineNumber,
  );

  return sortedRangesToRemove;
}
